import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/button';
import QuestionCircleIcon from '@atlaskit/icon/core/question-circle';
import Image from '@atlaskit/image/image';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import type { PaywallButtonActionTypes } from '@confluence/whiteboard-utils/entry-points/whiteboardConstants.ts';

import type { ChangeEditionSectionMessageProps } from '../SectionMessage/ChangeEditionSectionMessage.tsx';
import { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery.ts';
import { Card } from '../UpsellLooknFeel/Card.tsx';

import WhiteboardsPaywallImg from './images/WhiteboardsPaywall.svg';
import WhiteboardsPaywallDarkImg from './images/WhiteboardsPaywallDark.svg';
import WhiteboardsPaywallImgV2 from './images/WhiteboardsPaywallV2.svg';
import WhiteboardsPaywallDarkImgV2 from './images/WhiteboardsPaywallDarkV2.svg';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardWrapper = styled.div({
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: `0 0 ${token('radius.small', '3px')} ${token('radius.small', '3px')}`,
});

type RequiredSharedProps = Required<
	Pick<
		ChangeEditionSectionMessageProps,
		'learnMoreLink' | 'secondaryLink' | 'source' | 'title' | 'upgradeEditionActionSubjectId'
	>
>;

export type WhiteboardsActiveBoardLimitModalProps = React.PropsWithChildren<
	RequiredSharedProps & {
		learnMoreLink: Required<
			Pick<RequiredSharedProps['learnMoreLink'], 'href' | 'messageDescriptor'>
		>;
		onClose?: (action?: PaywallButtonActionTypes) => void;
		onCloseComplete?: () => void;
	}
>;

const getLinkAppearance = (
	isSiteAdmin: boolean,
	secondaryLink: WhiteboardsActiveBoardLimitModalProps['secondaryLink'],
) => {
	if (secondaryLink.appearance) {
		return secondaryLink.appearance;
	}
	return isSiteAdmin ? 'link' : 'primary';
};

const WhiteboardsActiveBoardLimitModalContents = ({
	children,
	learnMoreLink,
	secondaryLink,
	onClose,
	source,
	title,
	upgradeEditionActionSubjectId,
}: WhiteboardsActiveBoardLimitModalProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useContext(SPAViewContext);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const fireLearnMoreClickedAnalytics = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: learnMoreLink.actionSubjectId,
				source,
			},
		}).fire();
	};

	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const showLearnMoreButton = isSiteAdmin && !fg('ten_active_boards_in_standard');

	return (
		<CardWrapper>
			<Card
				body={<>{children}</>}
				learnMoreLink={{
					...learnMoreLink,
					appearance: learnMoreLink.appearance || 'link',
				}}
				secondaryLink={{
					...secondaryLink,
					appearance: getLinkAppearance(isSiteAdmin, secondaryLink),
				}}
				source={source}
				targetEdition={ConfluenceEdition.PREMIUM}
				title={title}
				upgradeEditionActionSubjectId={upgradeEditionActionSubjectId}
				headerButton={
					<>
						{showLearnMoreButton && (
							<Tooltip content={formatMessage(learnMoreLink.messageDescriptor)}>
								{(tooltipProps) => (
									<Button
										appearance="subtle-link"
										href={learnMoreLink.href}
										iconBefore={
											<QuestionCircleIcon
												label={formatMessage(learnMoreLink.messageDescriptor)}
												color={token('color.text.subtle')}
											/>
										}
										spacing="none"
										target="_blank"
										{...tooltipProps}
										onClick={(event) => {
											tooltipProps?.onClick?.(event);
											fireLearnMoreClickedAnalytics();
										}}
									/>
								)}
							</Tooltip>
						)}
						<CloseButton
							testId="whiteboards-active-board-modal-close-button"
							onClick={() => {
								onClose?.('close');
							}}
						/>
					</>
				}
			>
				<Image
					src={
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						fg('ten_active_boards_in_standard') ? WhiteboardsPaywallImgV2 : WhiteboardsPaywallImg
					}
					srcDark={
						// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
						fg('ten_active_boards_in_standard')
							? WhiteboardsPaywallDarkImgV2
							: WhiteboardsPaywallDarkImg
					}
					alt=""
					width="100%"
				/>
			</Card>
		</CardWrapper>
	);
};

const WrappedWhiteboardsActiveBoardLimitModalContents = withErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(WhiteboardsActiveBoardLimitModalContents);

export const WhiteboardsActiveBoardLimitModal = (
	props: WhiteboardsActiveBoardLimitModalProps,
): React.JSX.Element => (
	<Modal
		onClose={() => props.onClose?.('close')}
		onCloseComplete={props.onCloseComplete}
		shouldScrollInViewport
		testId="whiteboards-upsell-modal"
		width="440px"
	>
		<WrappedWhiteboardsActiveBoardLimitModalContents {...props} />
	</Modal>
);
